export const initAudioContext = async () => {
  try {
    const audioContext = new AudioContext()
    console.log('AudioContext state:', audioContext.state)
    await audioContext.resume()
    return {
      isSuccess: true,
      audioContext
    }
  } catch (error) {
    console.error('AudioContext failed:', error)
    return {
      isSuccess: false
    }
  }
}

export const detectChannels = async (audioUrl) => {
  try {
    const response = await fetch(audioUrl)
    const arrayBuffer = await response.arrayBuffer()

    const tempContext = new AudioContext()
    const audioBuffer = await tempContext.decodeAudioData(arrayBuffer)
    const count = audioBuffer.numberOfChannels
    tempContext.close()

    console.log('Detected channels:', count)
    if (count === 0) throw new Error('No channels found')

    return {
      isSuccess: true,
      count
    }
  } catch (error) {
    console.error('Channel detection failed:', error)
    return {
      isSuccess: false
    }
  }
}

export const registerChannelProcessorWorklet = async (audioContext) => {
  try {
    await audioContext.audioWorklet.addModule('/audio-channel-processor-module.js')
    console.log('Worklet registered successfully')
    return { isSuccess: true }
  } catch (error) {
    console.error('Worklet registration failed:', error)
    return { isSuccess: false }
  }
}

export const setupAudioGraph = async ({ audioContext, channelCount, audioElement }) => {
  try {
    await registerChannelProcessorWorklet(audioContext)

    // Create the worklet node with the channel configuration.
    const workletNode = new AudioWorkletNode(audioContext, 'channel-processor', {
      parameterData: { activeChannels: channelCount },
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount,
      channelCountMode: 'explicit',
      processorOptions: {
        // Add performance monitoring options
        monitorPerformance: true,
        bufferSize: 2048 // Increased buffer size for better stability
      }
    })

    // Start the message port (critical for receiving messages!)
    workletNode.port.start()

    // Create a splitter to separate the processed multichannel output
    const splitter = audioContext.createChannelSplitter(channelCount)

    // Create a summing node for downmixing with optimized settings
    const monoSum = audioContext.createGain()
    monoSum.channelCount = 1
    monoSum.channelCountMode = 'explicit'
    // Optionally, adjust monoSum.gain.value to scale summed amplitude if needed:
    // monoSum.gain.value = 1 / channelCount;

    // Connect nodes: Media element -> worklet -> splitter
    const mediaElementSource = audioContext.createMediaElementSource(audioElement)
    mediaElementSource.connect(workletNode)
    workletNode.connect(splitter)

    // Route each splitter output (each channel) into the mono sum node.
    for (let i = 0; i < channelCount; i++) {
      splitter.connect(monoSum, i, 0)
    }

    // Now we need to route the mono signal into both channels
    // so that the user hears identical audio in both ears.
    // Use a splitter to grab the mono signal and a merger to duplicate it.
    const monoSplitter = audioContext.createChannelSplitter(1)
    const stereoMerger = audioContext.createChannelMerger(2)

    monoSum.connect(monoSplitter)
    // Connect mono output into left and right channels of the stereo merger.
    monoSplitter.connect(stereoMerger, 0, 0) // left
    monoSplitter.connect(stereoMerger, 0, 1) // right

    // Connect the final stereo output to the destination.
    stereoMerger.connect(audioContext.destination)

    // Add performance monitoring
    let lastProcessTime = performance.now()
    workletNode.port.onmessage = (event) => {
      if (event.data.type === 'performance') {
        const now = performance.now()
        const processTime = now - lastProcessTime
        lastProcessTime = now

        if (processTime > 16) {
          // More than 16ms (60fps) is concerning
          console.warn('Audio processing taking too long:', {
            processTime,
            channelCount,
            bufferSize: workletNode.processorOptions?.bufferSize
          })
        }
      }
    }

    console.log('Audio nodes connected (optimized):', {
      source: !!mediaElementSource,
      worklet: !!workletNode,
      splitter: !!splitter,
      monoSum: !!monoSum,
      stereoMerger: !!stereoMerger,
      destination: !!audioContext.destination,
      channelCount,
      workletChannels: workletNode.channelCount
    })

    return {
      isSuccess: true,
      workletNode,
      mediaElementSource
    }
  } catch (error) {
    console.error('Audio graph setup failed:', error)
    return {
      isSuccess: false
    }
  }
}

export const updateChannelsCount = ({ workletNode, value }) => {
  if (!workletNode) return

  const param = workletNode.parameters.get(`activeChannels`)
  if (param) {
    param.value = value
    console.log(`Channel count:`, param.value)
  }
}

export const updateChannelVolume = ({ workletNode, index, value }) => {
  if (!workletNode) return

  const param = workletNode.parameters.get(`volume${index}`)
  if (param) {
    param.value = value
    console.log(`Channel ${index} volume:`, param.value)
  }
}
